import React from "react";

import {
  ArrowDownRight,
  ArrowUpRight,
  Bell,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Eye,
  Filter,
  Flag,
  MoreHorizontal,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Ticket,
  X,
} from "lucide-react";

import { DashboardLayout } from "@/components/layout";
import { OrderInsightDrawer } from "@/components/orders/OrderInsightDrawer";
import { OrdersSelectDropdown } from "@/components/orders/OrdersSelectDropdown";
import { OrdersSortTh } from "@/components/orders/OrdersSortTh";
import {
  Button,
  Card,
  CardContent,
  ConfirmationModal,
  PageHeader,
  showToast,
  StatusBadge,
} from "@/components/ui";
import { useOrdersPage } from "@/hooks/useOrdersPage";
import { cn } from "@/lib/utils";

export default function Orders() {
  const {
    search,
    statusFilter,
    mktFilter,
    sellerFilter,
    payFilter,
    riskFilter,
    slaFilter,
    syncFilter,
    disputeFilter,
    showAdvanced,
    setShowAdvanced,
    kpiFilter,
    page,
    selected,
    insightOrderId,
    bulkModal,
    sortCol,
    paginated,
    totalPages,
    totalCount,
    loading,
    sellerList,
    PAGE_SIZE,
    statusOptions,
    marketplaceOptions,
    paymentOptions,
    riskOptions,
    slaOptions,
    syncOptions,
    statusColors,
    riskColors,
    mktBadge,
    kpiDefs,
    iconBg,
    ordersKPIs,
    fmtVal,
    fmtDate,
    isUp,
    setSearch,
    setStatusFilter,
    setMktFilter,
    setSellerFilter,
    setPayFilter,
    setRiskFilter,
    setSlaFilter,
    setSyncFilter,
    setDisputeFilter,
    setKpiFilter,
    setPage,
    setSelected,
    setInsightOrderId,
    setBulkModal,
    toggleSort,
    toggleSelect,
    toggleAll,
    resetFilters,
    handleKPIClick,
    handleBulkAction,
    confirmBulk,
  } = useOrdersPage();

  const resetPage = () => setPage(1);

  return (
    <DashboardLayout activePath="/orders">
      <PageHeader
        title="Orders"
        subtitle="Monitor and manage orders across all sellers and marketplaces."
        breadcrumbs={[{ label: "Orders", active: true }]}
      />

      {/* ─── KPI Cards ─── */}
      <div
        data-testid="orders-kpi-grid"
        className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-6"
      >
        {kpiDefs.map((kpi) => {
          const Icon = kpi.icon;
          const d = ordersKPIs[kpi.key];
          const up = isUp(d.delta);
          const active = kpiFilter === kpi.filter;
          return (
            <Card
              key={kpi.key}
              data-testid={`kpi-${kpi.key}`}
              className={cn(
                "cursor-pointer transition-all duration-150",
                kpi.filter &&
                  "hover:border-indigo-300 dark:hover:border-indigo-700",
                active &&
                  "border-indigo-400 dark:border-indigo-600 ring-2 ring-indigo-500/20",
              )}
              onClick={() => handleKPIClick(kpi.filter)}
            >
              <CardContent className="p-3.5">
                <div className="flex items-center justify-between mb-2">
                  <div className={cn("p-1.5 rounded-md", iconBg[kpi.color])}>
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <span
                    className={cn(
                      "inline-flex items-center gap-0.5 text-[10px] font-medium",
                      up
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-rose-600 dark:text-rose-400",
                    )}
                  >
                    {up ? (
                      <ArrowUpRight className="w-3 h-3" />
                    ) : (
                      <ArrowDownRight className="w-3 h-3" />
                    )}
                    {d.delta}
                  </span>
                </div>
                <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                  {fmtVal(d.value, kpi.fmt)}
                </p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                  {kpi.label}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ─── Filters Row 1 ─── */}
      <div data-testid="orders-filters" className="space-y-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="orders-search"
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                resetPage();
              }}
              placeholder="Search order ID, customer, SKU, seller..."
              className="w-full pl-10 pr-4 py-2 text-xs rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
          </div>
          <OrdersSelectDropdown
            testId="filter-marketplace"
            value={mktFilter}
            onChange={(v) => {
              setMktFilter(v);
              resetPage();
            }}
            options={marketplaceOptions}
          />
          <OrdersSelectDropdown
            testId="filter-seller"
            value={sellerFilter}
            onChange={(v) => {
              setSellerFilter(v);
              resetPage();
            }}
            options={["All", ...sellerList]}
          />
          <OrdersSelectDropdown
            testId="filter-status"
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              resetPage();
            }}
            options={statusOptions}
          />
          <OrdersSelectDropdown
            testId="filter-payment"
            value={payFilter}
            onChange={(v) => {
              setPayFilter(v);
              resetPage();
            }}
            options={paymentOptions}
          />
          <Button
            data-testid="toggle-advanced"
            variant="outline"
            size="sm"
            onClick={() => setShowAdvanced((p) => !p)}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            {showAdvanced ? "Less" : "More"}
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              data-testid="export-csv-btn"
              variant="outline"
              size="sm"
              onClick={() =>
                showToast.info("Export", "CSV export started (mock).")
              }
            >
              <Download className="w-3.5 h-3.5" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* ─── Filters Row 2 (advanced) ─── */}
        {showAdvanced && (
          <div
            data-testid="advanced-filters"
            className="flex flex-wrap items-center gap-2 animate-in fade-in slide-in-from-top-1 duration-200"
          >
            <OrdersSelectDropdown
              testId="filter-risk"
              value={riskFilter}
              onChange={setRiskFilter}
              options={riskOptions}
            />
            <OrdersSelectDropdown
              testId="filter-sla"
              value={slaFilter}
              onChange={setSlaFilter}
              options={slaOptions}
            />
            <OrdersSelectDropdown
              testId="filter-sync"
              value={syncFilter}
              onChange={setSyncFilter}
              options={syncOptions}
            />
            <OrdersSelectDropdown
              testId="filter-dispute"
              value={disputeFilter}
              onChange={setDisputeFilter}
              options={["All", "Yes", "No"]}
            />
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              <X className="w-3.5 h-3.5" />
              Reset
            </Button>
          </div>
        )}
      </div>

      {/* ─── Bulk Action Bar ─── */}
      {selected.size > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="flex items-center gap-3 px-4 py-2.5 mb-3 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 animate-in fade-in slide-in-from-top-1 duration-200"
        >
          <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
            {selected.size} selected
          </span>
          <div className="h-4 w-px bg-indigo-200 dark:bg-indigo-700" />
          {[
            { label: "Resync Orders", icon: RefreshCw },
            { label: "Flag for Review", icon: Flag },
            { label: "Send Notification", icon: Bell },
            { label: "Export Selected", icon: Download },
            { label: "Create Ticket", icon: Ticket },
          ].map((a) => (
            <Button
              key={a.label}
              data-testid={`bulk-${a.label.toLowerCase().replace(/ /g, "-")}`}
              variant="ghost"
              size="sm"
              onClick={() => handleBulkAction(a.label)}
            >
              <a.icon className="w-3.5 h-3.5" />
              {a.label}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={() => setSelected(new Set())}
          >
            <X className="w-3.5 h-3.5" />
            Clear
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {loading ? "…" : `${totalCount} orders`}
        </span>
        {kpiFilter && (
          <button
            data-testid="clear-kpi-filter"
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
            onClick={() => setKpiFilter(null)}
          >
            Clear KPI filter
          </button>
        )}
      </div>

      {/* ─── Table ─── */}
      <Card>
        <div className="overflow-x-auto">
          <table
            data-testid="orders-table"
            className="w-full text-sm min-w-[1200px]"
          >
            <thead className="sticky top-0 z-10 bg-white dark:bg-slate-900">
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="px-3 py-2.5 w-10">
                  <input
                    data-testid="select-all"
                    type="checkbox"
                    checked={
                      paginated.length > 0 && selected.size === paginated.length
                    }
                    onChange={toggleAll}
                    className="rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500/20 h-3.5 w-3.5"
                  />
                </th>
                <OrdersSortTh col="id" sortCol={sortCol} onSort={toggleSort}>
                  Order ID
                </OrdersSortTh>
                <OrdersSortTh
                  col="seller"
                  sortCol={sortCol}
                  onSort={toggleSort}
                >
                  Seller
                </OrdersSortTh>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Marketplace
                </th>
                <OrdersSortTh
                  col="status"
                  sortCol={sortCol}
                  onSort={toggleSort}
                >
                  Status
                </OrdersSortTh>
                <OrdersSortTh col="date" sortCol={sortCol} onSort={toggleSort}>
                  Date
                </OrdersSortTh>
                <OrdersSortTh col="items" sortCol={sortCol} onSort={toggleSort}>
                  Items
                </OrdersSortTh>
                <OrdersSortTh col="gmv" sortCol={sortCol} onSort={toggleSort}>
                  GMV
                </OrdersSortTh>
                <OrdersSortTh col="net" sortCol={sortCol} onSort={toggleSort}>
                  Net Est.
                </OrdersSortTh>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Risk
                </th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td
                    colSpan={11}
                    className="px-4 py-16 text-center text-sm text-slate-400 dark:text-slate-500"
                  >
                    Loading…
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-16 text-center">
                    <div
                      data-testid="empty-state"
                      className="flex flex-col items-center gap-2"
                    >
                      <Filter className="w-8 h-8 text-slate-300 dark:text-slate-600" />
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        No orders match your filters
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={resetFilters}
                      >
                        Reset Filters
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((o) => (
                  <tr
                    key={o.id}
                    data-testid={`order-row-${o.id}`}
                    className={cn(
                      "hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors",
                      selected.has(o.id) &&
                        "bg-indigo-50/50 dark:bg-indigo-950/20",
                    )}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(o.id)}
                        onChange={() => toggleSelect(o.id)}
                        className="rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500/20 h-3.5 w-3.5"
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
                      {o.id}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-xs font-medium text-slate-900 dark:text-slate-100 truncate max-w-[120px]">
                        {o.seller}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono">
                        {o.sellerWsId}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={cn(
                          "px-1.5 py-0.5 text-[10px] font-medium rounded",
                          mktBadge[o.marketplace] ||
                            "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400",
                        )}
                      >
                        {o.marketplace}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge
                        status={statusColors[o.status] || "DISABLED"}
                        showDot
                      >
                        {o.status}
                      </StatusBadge>
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {fmtDate(o.date)}
                    </td>

                    <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-300 text-center">
                      {o.items}
                    </td>
                    <td className="px-3 py-2 text-xs font-medium text-slate-900 dark:text-slate-100 whitespace-nowrap">
                      {fmtVal(o.gmv, "currency")}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">
                      {fmtVal(o.net, "currency")}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={riskColors[o.risk]} showDot={false}>
                        {o.risk}
                      </StatusBadge>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-0.5">
                        <button
                          data-testid={`view-insight-${o.id}`}
                          onClick={() => setInsightOrderId(o.id)}
                          title="View Order Insight"
                          className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`open-detail-${o.id}`}
                          onClick={() =>
                            showToast.info(
                              "Open",
                              `Full details for ${o.id} (placeholder).`,
                            )
                          }
                          title="Open Details"
                          className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`more-${o.id}`}
                          onClick={() =>
                            showToast.info("More", `Actions for ${o.id}.`)
                          }
                          title="More"
                          className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                        >
                          <MoreHorizontal className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Showing {(page - 1) * PAGE_SIZE + 1}–
              {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const p = page <= 3 ? i + 1 : page + i - 2;
                if (p < 1 || p > totalPages) return null;
                return (
                  <Button
                    key={p}
                    variant={p === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPage(p)}
                    className={cn(
                      "min-w-[32px]",
                      p === page && "pointer-events-none",
                    )}
                  >
                    {p}
                  </Button>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* ─── Order Insight Drawer ─── */}
      <OrderInsightDrawer
        open={!!insightOrderId}
        onClose={() => setInsightOrderId(null)}
        orderId={insightOrderId}
      />

      {/* ─── Bulk Confirmation Modal ─── */}
      <ConfirmationModal
        open={!!bulkModal}
        onOpenChange={(open) => {
          if (!open) setBulkModal(null);
        }}
        title={bulkModal || ""}
        description={`Apply "${bulkModal}" to ${selected.size} selected orders? This action will be logged.`}
        confirmLabel={bulkModal || "Confirm"}
        variant={bulkModal === "Flag for Review" ? "danger" : "primary"}
        onConfirm={confirmBulk}
        onCancel={() => setBulkModal(null)}
      />
    </DashboardLayout>
  );
}
